import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Users, Building2, Package, AlertTriangle,
  CheckCircle2, ArrowRight, Plus, Filter, Download, MoreHorizontal, X,
  ExternalLink, Database, Settings2, Rocket,
} from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import { MODULES } from '@/lib/modules';
import api from '@/lib/api';
import { formatApiError } from '@/lib/auth';
import { usePlatformConfig } from '@/lib/platformConfig';

export default function ControlPanel() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [seeding, setSeeding] = useState(null);
  const [editOrg, setEditOrg] = useState(null);

  const load = async () => {
    try {
      const [s, o] = await Promise.all([
        api.get('/analytics/summary'),
        api.get('/orgs', { params: { q: search || undefined, limit: 20 } }),
      ]);
      setSummary(s.data);
      setOrgs(o.data.items);
    } catch (e) { console.error(e); }
  };

  const seedHR = async (orgId) => {
    if (!window.confirm(`Seed sample HR data (departments, employees, vacancies, courses) into ${orgId}?`)) return;
    setSeeding(orgId);
    try {
      const { data } = await api.post(`/hr/seed/${orgId}`);
      window.alert(`Seeded ${data.departments} departments, ${data.positions} positions, ${data.employees_created} employees.`);
      load();
    } catch (e) {
      window.alert(formatApiError(e) || 'Seed failed');
    } finally {
      setSeeding(null);
    }
  };

  useEffect(() => { load(); }, [search]);

  return (
    <DashboardShell sectionTitle="Overview">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
        <div>
          <div className="text-sm text-neutral-500 mb-1">{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' })}</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-navy">Platform Control</h1>
          <p className="text-neutral-600 mt-1">Live state of the ATTP Nexus platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-outline text-sm py-2"><Filter className="w-4 h-4" /> Filter</button>
          <button className="btn-outline text-sm py-2"><Download className="w-4 h-4" /> Export</button>
          <button onClick={() => setShowCreate(true)} className="btn-accent text-sm py-2" data-testid="cp-new-org"><Plus className="w-4 h-4" /> New Organization</button>
        </div>
      </div>

      <LaunchModeCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="kpi-grid">
        {[
          { icon: Building2, label: 'Active Organizations', value: summary?.active_organizations ?? '…', sub: `${summary?.total_organizations ?? 0} total`, accent: '#6B1F2B' },
          { icon: Users, label: 'Platform Users', value: summary?.platform_users ?? '…', sub: 'Across all orgs', accent: '#F59B2D' },
          { icon: Package, label: 'Modules Activated', value: summary?.modules_activated ?? '…', sub: 'Slots in use', accent: '#059669' },
          { icon: TrendingUp, label: 'Recurring Revenue', value: summary ? `₦${(summary.mrr / 1000000).toFixed(1)}M` : '…', sub: 'Monthly', accent: '#D4A437' },
        ].map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="kpi-tile" style={{ '--module-accent': k.accent }}>
              <div className="flex items-start justify-between mb-3">
                <div className="h-10 w-10 rounded-md flex items-center justify-center" style={{ background: `${k.accent}14`, color: k.accent }}>
                  <Icon className="w-5 h-5" strokeWidth={1.6} />
                </div>
              </div>
              <div className="font-display text-3xl font-bold text-navy tracking-tight tabular-nums">{k.value}</div>
              <div className="text-xs uppercase tracking-wider text-neutral-500 mt-1.5">{k.label}</div>
              <div className="text-[11px] text-neutral-400 mt-0.5">{k.sub}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-8">
        <div className="lg:col-span-5 bg-white border border-neutral-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="font-display font-semibold text-lg text-navy">Module activations</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Top modules across all organizations</p>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(summary?.per_module || {})
              .sort(([, a], [, b]) => b - a)
              .slice(0, 8)
              .map(([slug, count]) => {
                const m = MODULES.find(x => x.slug === slug);
                if (!m) return null;
                const max = Math.max(...Object.values(summary?.per_module || { x: 1 }));
                return (
                  <div key={slug}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full" style={{ background: m.accent }} />
                        <span className="text-sm font-medium text-navy">{m.short}</span>
                      </div>
                      <span className="font-mono text-xs text-neutral-600 tabular-nums">{count}</span>
                    </div>
                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(count / max) * 100}%`, background: m.accent }} />
                    </div>
                  </div>
                );
              })}
            {Object.keys(summary?.per_module || {}).length === 0 && (
              <p className="text-sm text-neutral-500 text-center py-6">No modules activated yet.</p>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 bg-white border border-neutral-200 rounded-md p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-semibold text-lg text-navy">System health</h3>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">
              <CheckCircle2 className="w-3 h-3" /> All systems normal
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'API Gateway', val: 99.98 },
              { label: 'Database', val: 99.99 },
              { label: 'Object storage', val: 99.97 },
              { label: 'Email delivery', val: 98.42 },
              { label: 'PDF generation', val: 99.92 },
              { label: 'AI inference', val: 99.85 },
            ].map((s) => (
              <div key={s.label} className="p-3 bg-neutral-50 rounded">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-neutral-700">{s.label}</span>
                  <span className="font-mono text-xs text-neutral-600 tabular-nums">{s.val}%</span>
                </div>
                <div className="h-1 bg-neutral-200 rounded-full overflow-hidden">
                  <div className={`h-full ${s.val > 99 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${s.val}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Organizations table */}
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="organizations-table">
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-display font-semibold text-lg text-navy">Organizations</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Showing {orgs.length} of {summary?.total_organizations ?? '…'}</p>
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or ID…" className="px-3 py-2 text-sm border border-neutral-200 rounded-md w-72 focus:border-navy outline-none" data-testid="org-search" />
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50/50">
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-6 py-3">Organization</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">ID</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Modules</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Users</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Plan</th>
              <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Country</th>
              <th className="text-right text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-6 py-3">MRR</th>
              <th className="text-right text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.id} className="border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors" data-testid={`org-row-${o.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-md bg-navy text-white flex items-center justify-center font-semibold text-xs">
                      {o.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                    </div>
                    <div className="font-medium text-navy text-sm">{o.name}</div>
                  </div>
                </td>
                <td className="px-3 py-4 font-mono text-xs text-neutral-600">{o.id}</td>
                <td className="px-3 py-4">
                  <div className="flex items-center gap-1 flex-wrap">
                    {(o.modules || []).slice(0, 5).map((slug) => {
                      const mod = MODULES.find(x => x.slug === slug);
                      if (!mod) return null;
                      const Icon = mod.icon;
                      return (
                        <button
                          key={slug}
                          onClick={() => navigate(`/dashboard/${slug}`)}
                          className="h-6 w-6 rounded-sm flex items-center justify-center hover:scale-110 transition-transform"
                          style={{ background: `${mod.accent}18`, color: mod.accent }}
                          title={`Open ${mod.short}`}
                          data-testid={`org-module-open-${o.id}-${slug}`}
                        >
                          <Icon className="w-3.5 h-3.5" strokeWidth={1.7} />
                        </button>
                      );
                    })}
                    {(o.modules || []).length > 5 && <span className="text-[10px] font-mono text-neutral-500 ml-1">+{o.modules.length - 5}</span>}
                  </div>
                </td>
                <td className="px-3 py-4 text-sm text-neutral-700">{o.user_count}</td>
                <td className="px-3 py-4">
                  <span className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-amber/10 text-amber-700">{o.plan}</span>
                </td>
                <td className="px-3 py-4 text-sm text-neutral-700">{o.country}</td>
                <td className="px-6 py-4 text-right font-mono text-sm text-navy tabular-nums font-medium">₦{(o.mrr || 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button
                      onClick={() => setEditOrg(o)}
                      className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border border-neutral-200 hover:border-navy hover:bg-navy hover:text-white transition-colors flex items-center gap-1"
                      title="Manage subscribed modules"
                      data-testid={`org-modules-${o.id}`}
                    >
                      <Settings2 className="w-3 h-3" />
                      Modules
                    </button>
                    {(o.modules || []).includes('hr') && (
                      <button
                        onClick={() => seedHR(o.id)}
                        disabled={seeding === o.id}
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border border-neutral-200 hover:border-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1"
                        title="Seed sample HR data"
                        data-testid={`seed-hr-${o.id}`}
                      >
                        <Database className="w-3 h-3" />
                        {seeding === o.id ? '…' : 'Seed HR'}
                      </button>
                    )}
                    {(o.modules || []).length > 0 && (
                      <button
                        onClick={() => navigate(`/dashboard/${o.modules[0]}`)}
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-navy text-white hover:bg-navy/90 transition-colors flex items-center gap-1"
                        title="Open first module workspace"
                        data-testid={`org-open-${o.id}`}
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {orgs.length === 0 && (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-neutral-500 text-sm">No organizations found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {editOrg && <EditOrgModulesModal org={editOrg} onClose={() => setEditOrg(null)} onSaved={() => { setEditOrg(null); load(); }} />}
    </DashboardShell>
  );
}

function EditOrgModulesModal({ org, onClose, onSaved }) {
  const [selected, setSelected] = useState(org.modules || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toggle = (slug) => setSelected((cur) => cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]);
  const save = async () => {
    setBusy(true); setError('');
    try {
      await api.patch(`/orgs/${org.id}`, { modules: selected });
      onSaved();
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6" data-testid="edit-modules-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display font-semibold text-xl text-navy">Manage modules — {org.name}</h3>
            <p className="text-sm text-neutral-500 mt-0.5">Toggle the platform modules this organisation has subscribed to. Their managers and team members will see only these.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-neutral-100"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
          {MODULES.map((m) => {
            const Icon = m.icon;
            const on = selected.includes(m.slug);
            return (
              <button key={m.slug} type="button" onClick={() => toggle(m.slug)}
                className={`text-left p-3 rounded border transition-all ${on ? 'border-navy bg-navy/5' : 'border-neutral-200 hover:border-neutral-300 bg-white'}`}
                data-testid={`mod-toggle-${m.slug}`}>
                <div className="flex items-start gap-2.5">
                  <div className="h-8 w-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${m.accent}18`, color: m.accent }}>
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm text-navy truncate">{m.name}</div>
                      {on && <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">{m.short || m.type}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        {error && <div className="mt-4 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">{error}</div>}
        <div className="flex items-center justify-between pt-5 mt-4 border-t">
          <div className="text-xs text-neutral-500">{selected.length} of {MODULES.length} module{selected.length === 1 ? '' : 's'} selected</div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-accent text-sm" data-testid="save-modules">{busy ? 'Saving…' : 'Save subscription'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateOrgModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', org_id: '', country: 'Nigeria', plan: 'growth', modules: [],
    manager_name: '', manager_email: '', manager_password: '',
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleMod = (s) => setForm((f) => ({ ...f, modules: f.modules.includes(s) ? f.modules.filter(x => x !== s) : [...f.modules, s] }));

  const submit = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      await api.post('/orgs', form);
      onCreated(); onClose();
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-md sm:rounded-md shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-org-modal">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="font-display font-semibold text-xl text-navy">New organization</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Provision a workspace and an Org Manager.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Organization name"><input required value={form.name} onChange={set('name')} className="inp" placeholder="Acme Academy" /></Field>
            <Field label="Workspace ID (slug)"><input required value={form.org_id} onChange={set('org_id')} className="inp font-mono" placeholder="acme-academy" pattern="[a-z0-9-]{3,40}" /></Field>
            <Field label="Country"><input required value={form.country} onChange={set('country')} className="inp" /></Field>
            <Field label="Plan">
              <select value={form.plan} onChange={set('plan')} className="inp">
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </Field>
          </div>

          <div>
            <div className="label-tag text-neutral-500 mb-2">Modules to activate</div>
            <div className="grid grid-cols-3 gap-1.5 max-h-44 overflow-y-auto p-2 bg-neutral-50 rounded">
              {MODULES.map(m => (
                <label key={m.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs ${form.modules.includes(m.slug) ? 'bg-white border border-navy' : 'hover:bg-white'}`}>
                  <input type="checkbox" checked={form.modules.includes(m.slug)} onChange={() => toggleMod(m.slug)} className="rounded" />
                  <span className="truncate">{m.short}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-3 border-t border-neutral-200">
            <div className="label-tag text-amber-600 mb-3">Org Manager</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name"><input required value={form.manager_name} onChange={set('manager_name')} className="inp" /></Field>
              <Field label="Email"><input required type="email" value={form.manager_email} onChange={set('manager_email')} className="inp" /></Field>
              <Field label="Initial password" className="col-span-2"><input required type="password" minLength={8} value={form.manager_password} onChange={set('manager_password')} className="inp" placeholder="Min 8 characters" /></Field>
            </div>
          </div>

          {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">{error}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button type="submit" disabled={busy} className="btn-accent text-sm disabled:opacity-60" data-testid="submit-create-org">
              {busy ? 'Creating…' : 'Create organization'}
            </button>
          </div>
        </form>
      </div>
      <style>{`.inp{width:100%;padding:0.6rem 0.75rem;border:1px solid #E5E7EB;border-radius:6px;background:white;outline:none;transition:border .15s}.inp:focus{border-color:#0F2A5C;box-shadow:0 0 0 2px rgba(15,42,92,0.1)}`}</style>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      {children}
    </label>
  );
}


function LaunchModeCard() {
  const { config, refresh } = usePlatformConfig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = async (key) => {
    setBusy(true); setError('');
    try {
      await api.put('/config', { [key]: !config[key] });
      await refresh();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-8 rounded-md border overflow-hidden" data-testid="launch-mode-card"
      style={{ borderColor: config.launch_mode ? '#059669' : '#F59B2D33', background: config.launch_mode ? '#F0FDF4' : '#FFFBEB' }}>
      <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-7 flex items-start gap-4">
          <div className="h-12 w-12 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: config.launch_mode ? '#05966920' : '#F59B2D20', color: config.launch_mode ? '#059669' : '#B45309' }}>
            <Rocket className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-1" style={{ color: config.launch_mode ? '#059669' : '#B45309' }}>
              {config.launch_mode ? 'Launch mode · ON' : 'Development mode'}
            </div>
            <h3 className="font-display font-semibold text-lg text-navy mb-1">
              {config.launch_mode ? 'Platform is running in production configuration.' : 'Ready to go live?'}
            </h3>
            <p className="text-sm text-neutral-700 leading-relaxed">
              {config.launch_mode
                ? 'All demo credentials, sample banners and development affordances are hidden from the public. Flip off to return the platform to demo/dev configuration.'
                : 'Enable Launch Mode to hide every demo credential, sample-data banner and development affordance across the platform in one switch. Production-only modules and branding will be shown.'}
            </p>
            {error && <div className="mt-3 text-xs text-rose-700">{error}</div>}
          </div>
        </div>
        <div className="lg:col-span-5 flex flex-col gap-2 lg:items-end">
          <button
            type="button"
            onClick={() => toggle('launch_mode')}
            disabled={busy}
            className="px-5 py-2.5 rounded-md font-semibold text-sm inline-flex items-center gap-2 transition-transform hover:scale-[1.03] disabled:opacity-60"
            style={{ background: config.launch_mode ? '#DC2626' : '#059669', color: '#FFFFFF' }}
            data-testid="launch-mode-toggle"
          >
            <Rocket className="w-4 h-4" />
            {busy ? 'Applying…' : (config.launch_mode ? 'Disable Launch Mode' : 'Enable Launch Mode')}
          </button>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold text-right">
            Show demo credentials: <span className={config.show_demo_credentials ? 'text-emerald-700' : 'text-neutral-400'}>{config.show_demo_credentials ? 'yes' : 'hidden'}</span>
            <span className="mx-1.5">·</span>
            Show sample banners: <span className={config.show_sample_data_banner ? 'text-emerald-700' : 'text-neutral-400'}>{config.show_sample_data_banner ? 'yes' : 'hidden'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
